import React from "react";
import dayjs from "dayjs";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { NOTE_TYPE_VALUE } from "../../utils";
import { notesApi } from "../../services/notesApi";
import {
  dashboardFields,
  dashboardHeaders,
  osoDepartmentHeaders,
  osoDepartmentFields,
  osoPlanogramHeaders,
  osoPlanogramFields,
  osoItemHeaders,
  osoItemFields,
  ssPlanogramFields,
  ssPlanogramHeaders,
  ssItemFields,
  ssItemHeaders,
} from "./mappings";
import getValueMapper from "./valueMapper";

const exportButtonSx = {
  "& .MuiButton-endIcon": {
    margin: 0,
  },
  "& .MuiSvgIcon-root": {
    fontSize: 16,
  },
};

const getFileMetadata = (store, departmentLetter, pogId) => ({
  dashboard: {
    fileName: `Projects-Report-${dayjs().format("MM/DD/YYYY")}`,
    sheetName: "Projects Report",
    headers: dashboardHeaders,
    fields: dashboardFields,
  },
  osoDepartment: {
    fileName: `OSO-Department-Report${
      !!store?.memberNumber && `-${store?.memberNumber}`
    }-${dayjs().format("MM/DD/YYYY")}`,
    sheetName: "Departments Report",
    headers: osoDepartmentHeaders,
    fields: osoDepartmentFields,
  },
  osoPlanogram: {
    fileName: `OSO-Planogram-Report-${departmentLetter}${
      !!store?.memberNumber && `-${store?.memberNumber}`
    }-${dayjs().format("MM/DD/YYYY")}`,
    sheetName: "Planograms Report",
    headers: osoPlanogramHeaders,
    fields: osoPlanogramFields,
  },
  osoItem: {
    fileName: `OSO-Item-Report-${departmentLetter}-${pogId}${
      !!store?.memberNumber && `-${store?.memberNumber}`
    }-${dayjs().format("MM/DD/YYYY")}`,
    sheetName: "Items Report",
    headers: osoItemHeaders,
    fields: osoItemFields,
  },
  ssPlanogram: {
    fileName: `Store-Supplies-Planogram-Report${
      !!store?.memberNumber && `-${store?.memberNumber}`
    }-${dayjs().format("MM/DD/YYYY")}`,
    sheetName: "Store Supplies Planogram Report",
    headers: ssPlanogramHeaders,
    fields: ssPlanogramFields,
  },
  ssItem: {
    fileName: `Store-Supplies-Item-Report-${departmentLetter}-${pogId}${
      !!store?.memberNumber && `-${store?.memberNumber}`
    }-${dayjs().format("MM/DD/YYYY")}`,
    sheetName: "Items Report",
    headers: ssItemHeaders,
    fields: ssItemFields,
  },
});

const FileExport = ({ type, data = [], isXDept = false }) => {
  const store = useSelector((store) => store.global.store);
  const { departmentLetter, pogId } = useParams();
  const { data: plannerNotesData } =
    type === "osoPlanogram"
      ? notesApi.endpoints.getNotesForPlanogram.useQuery({
          noteType: NOTE_TYPE_VALUE.planner,
          storeId: store.id,
        })
      : { data: [] };

  const { data: retailerNotesData } =
    type === "osoPlanogram"
      ? notesApi.endpoints.getNotesForPlanogram.useQuery({
          noteType: NOTE_TYPE_VALUE.retailer,
          storeId: store.id,
        })
      : { data: [] };

  const workbook = new Excel.Workbook();
  const fileMetadata = getFileMetadata(store, departmentLetter, pogId);
  const metadata = fileMetadata[type];

  const columns = metadata.headers?.map((header, i) => ({
    header: header,
    key: metadata.fields[i],
    // If UPC padded 0 will remain & it will not get converted to exponential number format.
    ...(metadata.fields[i] === "UPC"
      ? {
          style: { numFmt: "Text" },
        }
      : {}),
  }));

  const handleDownloadExcel = async () => {
    try {
      const worksheet = workbook.addWorksheet(metadata.sheetName);
      worksheet.columns = columns;
      worksheet.getRow(1).font = { bold: true };

      worksheet.columns.forEach((column) => {
        column.width = Math.max(column.header.length + 5, 20);
        column.alignment = {
          horizontal: "left",
        };
      });

      const valueMapper = getValueMapper(
        metadata.fields,
        store,
        plannerNotesData,
        retailerNotesData,
        type === "dashboard",
        type
      );

      data.forEach((singleData) => {
        const _row = valueMapper(singleData);
        worksheet.addRow(_row);
      });

      worksheet.eachRow({ includeEmpty: false }, (row) => {
        const currentCell = row._cells;
        currentCell.forEach((singleCell) => {
          const cellAddress = singleCell._address;
          worksheet.getCell(cellAddress).border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      const buf = await workbook.xlsx.writeBuffer();

      saveAs(new Blob([buf]), `${metadata.fileName}.xlsx`);
    } catch (error) {
      console.log(error);
    } finally {
      workbook.removeWorksheet(metadata.sheetName);
    }
  };

  return (
    <Tooltip title="Download Excel (.xlsx)">
      <Button
        id="export-btn"
        disableElevation
        size="small"
        onClick={handleDownloadExcel}
        endIcon={<FileDownloadOutlinedIcon />}
        sx={exportButtonSx}
      >
        Export
      </Button>
    </Tooltip>
  );
};

export default FileExport;
